import ApiError from "@/model/ApiError";
import ErrorResponse from "@/model/ErrorResponse";
import API_BASE_URL from "@/config/apiConfig";

async function throwIfError(response: Response): Promise<void> {
  if (!response.ok) {
    const errorBody = (await response.json().catch(() => null)) as ErrorResponse | null;
    throw new ApiError(
      response.status,
      errorBody?.message ?? response.statusText
    );
  }
}

async function handleResponse<TResponse>(response: Response): Promise<TResponse> {
  await throwIfError(response);
  return response.json() as Promise<TResponse>;
}

function authHeaders(accessToken?: string): HeadersInit {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

async function postJson<TResponse>(
  path: string,
  body: unknown,
  accessToken?: string
): Promise<TResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(accessToken),
    },
    body: JSON.stringify(body),
  });

  return handleResponse<TResponse>(response);
}

async function getJson<TResponse>(
  path: string,
  accessToken?: string
): Promise<TResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    headers: {
      ...authHeaders(accessToken),
    },
  });

  return handleResponse<TResponse>(response);
}

async function patchJson<TResponse>(
  path: string,
  body: unknown,
  accessToken?: string
): Promise<TResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(accessToken),
    },
    body: JSON.stringify(body),
  });

  return handleResponse<TResponse>(response);
}

async function putJson<TResponse>(
  path: string,
  accessToken?: string
): Promise<TResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "PUT",
    headers: {
      ...authHeaders(accessToken),
    },
  });

  return handleResponse<TResponse>(response);
}

async function putJsonBody<TResponse>(
  path: string,
  body: unknown,
  accessToken?: string
): Promise<TResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(accessToken),
    },
    body: JSON.stringify(body),
  });

  return handleResponse<TResponse>(response);
}

async function deleteJson<TResponse>(
  path: string,
  accessToken?: string
): Promise<TResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "DELETE",
    headers: {
      ...authHeaders(accessToken),
    },
  });

  return handleResponse<TResponse>(response);
}

async function deleteNoContent(
  path: string,
  accessToken?: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "DELETE",
    headers: {
      ...authHeaders(accessToken),
    },
  });

  await throwIfError(response);
}

async function putNoContent(
  path: string,
  body: unknown,
  accessToken?: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(accessToken),
    },
    body: JSON.stringify(body),
  });

  await throwIfError(response);
}

const httpClient = {
  postJson,
  getJson,
  patchJson,
  putJson,
  putJsonBody,
  deleteJson,
  putNoContent,
  deleteNoContent,
};

export default httpClient;
